import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFeatures } from '@/contexts/FeaturesContext'
import { apiClient } from '@/utils/api'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import toast from 'react-hot-toast'
import {
  Users, Calendar, School, ChevronDown, ChevronRight,
  Save, X, Edit2, Plus, Trash2, User, Lock, Mail, Phone,
  MapPin, BookOpen, GraduationCap, Baby, UserCheck, Home
} from 'lucide-react'
import { format, parseISO, differenceInYears } from 'date-fns'

interface UserProfile {
  id: string
  username: string
  display_name: string
  birthdate: string | null
  age: number
  account_type: string
  is_parent: boolean
  login_enabled: boolean
  email: string | null
  phone_number: string | null
  school_id: string | null
  school_name: string | null
  school_district: string | null
  primary_place_id: string | null
  primary_place_name: string | null
  notes: string | null
  color_theme: string
  avatar_url: string | null
  points: {
    total: number
    weekly: number
    level: number
    streak_days: number
  }
  oauth_accounts: Array<{
    id: string
    provider: string
    email: string
    linked_at: string
  }>
  preferences: Record<string, any>
}

interface School {
  id: string
  name: string
  district: string
  type: string
  address?: string
  phone?: string
  website?: string
  calendar_url?: string
}

interface Place {
  id: string
  name: string
  place_type: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
  phone_number?: string
  notes?: string
}

const ACCOUNT_TYPES = [
  { value: 'adult', label: 'Adult', icon: <UserCheck className="w-4 h-4" />, minAge: 18 },
  { value: 'college', label: 'College', icon: <GraduationCap className="w-4 h-4" />, minAge: 18 },
  { value: 'high_school', label: 'High School', icon: <School className="w-4 h-4" />, minAge: 14 },
  { value: 'middle_school', label: 'Middle School', icon: <BookOpen className="w-4 h-4" />, minAge: 11 },
  { value: 'primary_school', label: 'Elementary', icon: <BookOpen className="w-4 h-4" />, minAge: 5 },
  { value: 'toddler', label: 'Toddler', icon: <Baby className="w-4 h-4" />, minAge: 0 }
]

const AVATAR_EMOJIS = ['👤', '👦', '👧', '🧒', '👨', '👩', '🦸', '🧙', '🧚', '🦊', '🐱', '🐶', '🦁', '🐼', '🦄', '🐲']

const PLACE_TYPES = [
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'school', label: 'School' },
  { value: 'other_parent', label: "Other Parent's House" },
  { value: 'relative', label: "Relative's House" },
  { value: 'friend', label: "Friend's House" },
  { value: 'other', label: 'Other' }
]

// HTML5 <input type="time"> only accepts "HH:mm" or "HH:mm:ss".
// Phoenix/Postgrex serializes %Time{} as "17:00:00.000000" (with
// microseconds), which the browser silently rejects — the input
// renders blank, making it look like the value didn't save. Normalize
// any incoming time string to "HH:mm" so the input accepts it.
const toTimeInput = (v: string | null | undefined): string => {
  if (!v) return ''
  const s = String(v)
  // Already in HH:mm shape (length 5) — passthrough
  if (s.length === 5) return s
  // Longer: grab just the hours + minutes
  return s.slice(0, 5)
}

export default function UserManagementComplete() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [places, setPlaces] = useState<Place[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'users' | 'places'>('users')
  
  // User editing state
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [userFormData, setUserFormData] = useState<Partial<UserProfile>>({})
  const [showNewUser, setShowNewUser] = useState(false)
  const [newUserData, setNewUserData] = useState<{
    username: string
    display_name: string
    is_parent: boolean
    role: 'parent' | 'caretaker' | 'child'
    password?: string
    email?: string
    color_theme: string
    login_enabled: boolean
    emoji?: string
    birthdate?: string
  }>({ username: '', display_name: '', is_parent: false, role: 'child', color_theme: '#3498db', login_enabled: true, emoji: '👤' })
  
  // Place editing state
  const [showNewPlace, setShowNewPlace] = useState(false)
  const [editingPlace, setEditingPlace] = useState<string | null>(null)
  const [placeFormData, setPlaceFormData] = useState<Partial<Place>>({})

  // Kid password management state
  const [kidPasswordAuthEnabled, setKidPasswordAuthEnabled] = useState(false)
  const [passwordManagingUser, setPasswordManagingUser] = useState<string | null>(null)
  const [newKidPassword, setNewKidPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)
  const [userAuthStatus, setUserAuthStatus] = useState<Record<string, { password_enabled: boolean; has_oauth: boolean }>>({})

  const { user } = useAuth()
  const { isFeatureEnabled } = useFeatures()

  useEffect(() => {
    if (user?.is_parent) {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    setLoading(true)
    try {
      // Check kid password auth status
      try {
        const authStatus = await apiClient.getKidPasswordAuthStatus()
        setKidPasswordAuthEnabled(authStatus.enabled)
      } catch (err) {
        console.log('Kid password auth status not available')
      }

      // Build parallel requests array based on enabled features
      const requests = [
        apiClient.get('/api/enhanced/users/full').catch(() =>
          apiClient.get('/api/management/users').catch(() => ({ children: [] }))
        ),
        apiClient.get('/api/management/places').catch(() => ({ places: [] }))
      ]

      requests.push(apiClient.get('/api/schools').catch(() => ({ schools: [] })))

      const responses = await Promise.all(requests)
      const usersResponse = responses[0]
      const placesResponse = responses[1]
      const schoolsResponse = responses[2]

      // Handle wrapped responses - backend may return {children: [...]} or {users: [...]} or direct arrays
      const usersData = Array.isArray(usersResponse)
        ? usersResponse
        : (usersResponse?.children || usersResponse?.users || [])

      const placesData = Array.isArray(placesResponse)
        ? placesResponse
        : (placesResponse?.places || [])

      const schoolsData = Array.isArray(schoolsResponse)
        ? schoolsResponse
        : (schoolsResponse?.schools || [])

      setUsers(usersData)
      setSchools(schoolsData)
      setPlaces(placesData)
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load management data')
    } finally {
      setLoading(false)
    }
  }

  const toggleUserExpanded = (userId: string) => {
    const newExpanded = new Set(expandedUsers)
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId)
      setEditingUser(null)
    } else {
      newExpanded.add(userId)
      // Load auth status for kids when kid password auth is enabled
      const targetUser = users.find(u => u.id === userId)
      if (kidPasswordAuthEnabled && targetUser && !targetUser.is_parent) {
        loadUserAuthStatus(userId)
      }
    }
    setExpandedUsers(newExpanded)
  }

  const startEditUser = (user: UserProfile) => {
    setEditingUser(user.id)
    // Parse birthdate to YYYY-MM-DD for date input (handles both date-only and datetime strings)
    const birthdateValue = user.birthdate ? extractDateInTimezoneSync(user.birthdate) : ''
    // Derive account_type from is_parent if not set
    let accountType = user.account_type
    if (!accountType) {
      if (user.is_parent) {
        accountType = 'adult'
      } else {
        // Derive from age if available
        const age = calculateAgeFromBirthdate(user.birthdate)
        if (age !== null) {
          if (age >= 18) accountType = 'adult'
          else if (age >= 14) accountType = 'high_school'
          else if (age >= 11) accountType = 'middle_school'
          else if (age >= 5) accountType = 'primary_school'
          else accountType = 'toddler'
        } else {
          accountType = 'primary_school' // Default for children
        }
      }
    }
    setUserFormData({
      display_name: user.display_name,
      birthdate: birthdateValue,
      account_type: accountType,
      login_enabled: user.login_enabled,
      is_parent: user.is_parent,
      role: (user as any).role || (user.is_parent ? 'parent' : 'child'),
      email: user.email,
      phone_number: user.phone_number,
      school: (user as any).school,
      school_id: user.school_id,
      primary_place_id: user.primary_place_id,
      notes: user.notes,
      grade_level: (user as any).grade_level,
      teacher_name: (user as any).teacher_name,
      classroom: (user as any).classroom,
      student_id: (user as any).student_id,
      transport_mode: (user as any).transport_mode,
      bus_number: (user as any).bus_number,
      bus_pickup_time: (user as any).bus_pickup_time,
      bus_dropoff_time: (user as any).bus_dropoff_time,
      car_pickup_time: (user as any).car_pickup_time,
      car_dropoff_time: (user as any).car_dropoff_time,
      school_start_time: (user as any).school_start_time,
      school_end_time: (user as any).school_end_time,
      allergies: (user as any).allergies,
      medical_notes: (user as any).medical_notes,
      work_start_time: (user as any).work_start_time,
      work_end_time: (user as any).work_end_time,
      work_days: (user as any).work_days,
      work_location: (user as any).work_location,
      work_from_home: (user as any).work_from_home,
      work_schedule_notes: (user as any).work_schedule_notes,
    } as any)
  }

  const saveUser = async (userId: string) => {
    try {
      // Try enhanced endpoint first, fall back to basic
      try {
        await apiClient.put(`/api/enhanced/users/${userId}`, userFormData)
      } catch {
        // Fall back to basic management endpoint
        await apiClient.put(`/api/management/users/${userId}`, userFormData)
      }

      toast.success('User updated successfully')
      setEditingUser(null)
      loadData()
    } catch (error: any) {
      console.error('Failed to save user:', error)
      // Surface Phoenix 422 validation messages to the user
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          'Failed to save user'
      )
    }
  }

  const handleDeleteUser = async (targetUser: UserProfile) => {
    // Prevent deleting yourself
    if (targetUser.id === user?.id) {
      toast.error('You cannot delete your own account')
      return
    }

    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete ${targetUser.display_name}?\n\nThis will permanently remove their account, points history, and all associated data. This action cannot be undone.`
    )

    if (!confirmed) return

    try {
      await apiClient.deleteUser(targetUser.id)
      toast.success(`${targetUser.display_name} has been removed`)
      loadData()
    } catch (error: any) {
      console.error('Failed to delete user:', error)
      toast.error(error.response?.data?.error || 'Failed to delete user')
    }
  }

  // Kid password management functions
  const loadUserAuthStatus = async (userId: string) => {
    try {
      const status = await apiClient.getUserAuthStatus(userId)
      setUserAuthStatus(prev => ({ ...prev, [userId]: { password_enabled: status.password_enabled, has_oauth: status.has_oauth } }))
    } catch (err) {
      console.error('Failed to load user auth status:', err)
    }
  }

  const handleSetKidPassword = async (userId: string) => {
    if (newKidPassword.length < 4) {
      toast.error('Password must be at least 4 characters')
      return
    }

    setSavingPassword(true)
    try {
      await apiClient.setKidPassword(userId, newKidPassword)
      toast.success('Password set successfully')
      setPasswordManagingUser(null)
      setNewKidPassword('')
      loadUserAuthStatus(userId)
    } catch (err: any) {
      console.error('Failed to set password:', err)
      toast.error(err.response?.data?.error || 'Failed to set password')
    } finally {
      setSavingPassword(false)
    }
  }

  const handleDisableKidPassword = async (userId: string) => {
    setSavingPassword(true)
    try {
      await apiClient.disableKidPassword(userId)
      toast.success('Password login disabled')
      loadUserAuthStatus(userId)
    } catch (err: any) {
      console.error('Failed to disable password:', err)
      toast.error(err.response?.data?.error || 'Failed to disable password')
    } finally {
      setSavingPassword(false)
    }
  }

  const createUser = async () => {
    if (!newUserData.username || !newUserData.display_name) {
      toast.error('Username and display name are required')
      return
    }

    try {
      await apiClient.post('/api/users', {
        username: newUserData.username,
        display_name: newUserData.display_name,
        is_parent: newUserData.role !== 'child',
        role: newUserData.role,
        password: newUserData.password || null,
        email: newUserData.email || null,
        color_theme: newUserData.color_theme,
        login_enabled: newUserData.login_enabled,
        emoji: newUserData.emoji || null,
        birthdate: newUserData.birthdate || null
      })

      toast.success('User created successfully')
      setShowNewUser(false)
      setNewUserData({ username: '', display_name: '', is_parent: false, role: 'child', color_theme: '#3498db', login_enabled: true, emoji: '👤' })
      loadData()
    } catch (error: any) {
      console.error('Failed to create user:', error)
      if (error?.response?.status === 409) {
        toast.error('Username already exists')
      } else {
        toast.error('Failed to create user')
      }
    }
  }

  const savePlace = async () => {
    try {
      if (editingPlace === 'new') {
        await apiClient.post('/api/management/places', placeFormData)
        toast.success('Place added successfully')
      } else {
        await apiClient.put(`/api/management/places/${editingPlace}`, placeFormData)
        toast.success('Place updated successfully')
      }
      setEditingPlace(null)
      setPlaceFormData({})
      setShowNewPlace(false)
      loadData()
    } catch (error) {
      console.error('Failed to save place:', error)
      toast.error('Failed to save place')
    }
  }

  const deletePlace = async (placeId: string) => {
    if (!confirm('Are you sure you want to delete this place?')) return
    
    try {
      await apiClient.delete(`/api/management/places/${placeId}`)
      toast.success('Place deleted successfully')
      loadData()
    } catch (error) {
      console.error('Failed to delete place:', error)
      toast.error('Failed to delete place')
    }
  }

  const getAccountTypeIcon = (type: string) => {
    const accountType = ACCOUNT_TYPES.find(t => t.value === type)
    return accountType?.icon || <User className="w-4 h-4" />
  }

  const getAccountTypeLabel = (type: string) => {
    const accountType = ACCOUNT_TYPES.find(t => t.value === type)
    return accountType?.label || type
  }

  const calculateAgeFromBirthdate = (birthdate: string | null) => {
    if (!birthdate) return null
    try {
      return differenceInYears(new Date(), parseISO(birthdate))
    } catch {
      return null
    }
  }

  const renderUserCard = (user: UserProfile) => {
    const isExpanded = expandedUsers.has(user.id)
    const isEditing = editingUser === user.id
    const age = calculateAgeFromBirthdate(user.birthdate)

    return (
      <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => toggleUserExpanded(user.id)}
        >
          <div className="flex items-center space-x-3">
            {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: user.color_theme || '#3498db' }}
            >
              {user.display_name[0]}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-900">{user.display_name}</h3>
                {getAccountTypeIcon(user.account_type)}
                {!user.login_enabled && <Lock className="w-4 h-4 text-gray-400" />}
              </div>
              <div className="text-sm text-gray-500">
                @{user.username} • {age ? `${age} years old` : 'Age not set'} • {getAccountTypeLabel(user.account_type)}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {user.oauth_accounts?.length > 0 && (
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-xs text-gray-500">{user.oauth_accounts[0].email}</span>
              </div>
            )}
            {(user as any).role === 'caretaker' ? (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">Caretaker</span>
            ) : user.is_parent ? (
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">Parent</span>
            ) : null}
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 p-4">
            {isEditing ? (
              // Edit Form
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={userFormData.display_name || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, display_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Birthdate
                    </label>
                    <input
                      type="date"
                      value={userFormData.birthdate || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, birthdate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type
                    </label>
                    <select
                      value={userFormData.account_type || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, account_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {ACCOUNT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={userFormData.email || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={userFormData.phone_number || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {!userFormData.is_parent && <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School
                    </label>
                    <input
                      type="text"
                      list="school-options"
                      value={(userFormData as any).school || ''}
                      onChange={(e) => {
                        const match = schools.find(s => s.name === e.target.value)
                        setUserFormData({
                          ...userFormData,
                          school: e.target.value || null,
                          school_id: match?.id || null
                        } as any)
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type or select a school"
                    />
                    <datalist id="school-options">
                      {schools.map(school => (
                        <option key={school.id} value={school.name} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                    <select
                      value={(userFormData as any).grade_level || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, grade_level: e.target.value || null } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select</option>
                      {['Preschool', 'PreK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'College'].map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                    <input
                      type="text"
                      value={(userFormData as any).teacher_name || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, teacher_name: e.target.value } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Teacher name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Start Time</label>
                    <input
                      type="time"
                      value={toTimeInput((userFormData as any).school_start_time)}
                      onChange={(e) => setUserFormData({ ...userFormData, school_start_time: e.target.value || null } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School End Time</label>
                    <input
                      type="time"
                      value={toTimeInput((userFormData as any).school_end_time)}
                      onChange={(e) => setUserFormData({ ...userFormData, school_end_time: e.target.value || null } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Room #</label>
                    <input
                      type="text"
                      value={(userFormData as any).classroom || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, classroom: e.target.value } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 204"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Student ID</label>
                    <input
                      type="text"
                      value={(userFormData as any).student_id || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, student_id: e.target.value } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="School student ID"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transport Mode</label>
                    <select
                      value={(userFormData as any).transport_mode || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, transport_mode: e.target.value || null } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not Set</option>
                      <option value="bus">School Bus</option>
                      <option value="car">Car</option>
                      <option value="parent_car">Parent Car</option>
                      <option value="carpool">Carpool</option>
                      <option value="walk">Walk</option>
                      <option value="drives_self">Drives Self</option>
                      <option value="friend_pickup">Friend Pickup</option>
                    </select>
                  </div>

                  {/* Show transit-specific time fields only when the selected
                      mode could actually use them. `!mode` covers the
                      "mode unset but values saved" case that motivated the
                      v6.0.62 unconditional render. `walk`/`bike`/`drives_self`/
                      `friend_pickup` are transit-free; hide the fields. */}
                  {(() => {
                    const mode = (userFormData as any).transport_mode as string | null | undefined
                    const busModes = new Set(['bus'])
                    const carModes = new Set(['car', 'parent_car', 'carpool'])
                    const showBus = !mode || busModes.has(mode)
                    const showCar = !mode || carModes.has(mode)
                    return (
                      <>
                        {showBus && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Bus Number</label>
                              <input
                                type="text"
                                value={(userFormData as any).bus_number || ''}
                                onChange={(e) => setUserFormData({ ...userFormData, bus_number: e.target.value || null } as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., 123"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Bus Pickup Time</label>
                              <input
                                type="time"
                                value={toTimeInput((userFormData as any).bus_pickup_time)}
                                onChange={(e) => setUserFormData({ ...userFormData, bus_pickup_time: e.target.value || null } as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Bus Dropoff Time</label>
                              <input
                                type="time"
                                value={toTimeInput((userFormData as any).bus_dropoff_time)}
                                onChange={(e) => setUserFormData({ ...userFormData, bus_dropoff_time: e.target.value || null } as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </>
                        )}
                        {showCar && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Car Pickup Time</label>
                              <input
                                type="time"
                                value={toTimeInput((userFormData as any).car_pickup_time)}
                                onChange={(e) => setUserFormData({ ...userFormData, car_pickup_time: e.target.value || null } as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Car Dropoff Time</label>
                              <input
                                type="time"
                                value={toTimeInput((userFormData as any).car_dropoff_time)}
                                onChange={(e) => setUserFormData({ ...userFormData, car_dropoff_time: e.target.value || null } as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )
                  })()}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                    <textarea
                      value={(userFormData as any).allergies || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, allergies: e.target.value } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Food allergies, medication allergies, etc."
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Notes</label>
                    <textarea
                      value={(userFormData as any).medical_notes || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, medical_notes: e.target.value } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Medical notes and conditions"
                      rows={2}
                    />
                  </div>
                  </>}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Place
                    </label>
                    <select
                      value={userFormData.primary_place_id || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, primary_place_id: e.target.value || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Primary Place</option>
                      {places.map(place => (
                        <option key={place.id} value={place.id}>
                          {place.name} ({PLACE_TYPES.find(t => t.value === place.place_type)?.label || place.place_type})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userFormData.login_enabled ?? true}
                      onChange={(e) => setUserFormData({ ...userFormData, login_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Login Enabled</span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={(userFormData as any).role || (userFormData.is_parent ? 'parent' : 'child')}
                      onChange={(e) => {
                        const role = e.target.value
                        setUserFormData({ ...userFormData, is_parent: role !== 'child', role } as any)
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="child">Child</option>
                      <option value="parent">Parent (full access)</option>
                      <option value="caretaker">Caretaker (limited access)</option>
                    </select>
                  </div>
                </div>

                {/* Work Schedule — optional for anyone, usually parents/caretakers */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-2 flex items-center">
                    Work Schedule
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Optional. The kiosk uses these values to auto-show "At Work" or "Working from home" during the hours below.
                  </p>

                  <div className="mb-3 flex items-center">
                    <input
                      id={`wfh-${user.id}`}
                      type="checkbox"
                      checked={!!(userFormData as any).work_from_home}
                      onChange={(e) => setUserFormData({ ...userFormData, work_from_home: e.target.checked } as any)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`wfh-${user.id}`} className="ml-2 text-sm text-gray-700">
                      Works from home
                    </label>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Days</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { n: 1, label: 'Mon' },
                        { n: 2, label: 'Tue' },
                        { n: 3, label: 'Wed' },
                        { n: 4, label: 'Thu' },
                        { n: 5, label: 'Fri' },
                        { n: 6, label: 'Sat' },
                        { n: 7, label: 'Sun' },
                      ].map(({ n, label }) => {
                        const days: number[] = (userFormData as any).work_days ?? [1, 2, 3, 4, 5]
                        const active = days.includes(n)
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => {
                              const current: number[] = (userFormData as any).work_days ?? [1, 2, 3, 4, 5]
                              const next = active
                                ? current.filter(d => d !== n)
                                : [...current, n].sort((a, b) => a - b)
                              setUserFormData({ ...userFormData, work_days: next } as any)
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm border transition ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Work Start Time</label>
                      <input
                        type="time"
                        value={toTimeInput((userFormData as any).work_start_time)}
                        onChange={(e) => setUserFormData({ ...userFormData, work_start_time: e.target.value || null } as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Work End Time</label>
                      <input
                        type="time"
                        value={toTimeInput((userFormData as any).work_end_time)}
                        onChange={(e) => setUserFormData({ ...userFormData, work_end_time: e.target.value || null } as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Work Location</label>
                    <input
                      type="text"
                      list="work-location-options"
                      value={(userFormData as any).work_location || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, work_location: e.target.value || null } as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type or select — e.g., Home office, Downtown office"
                    />
                    <datalist id="work-location-options">
                      {places.map(place => (
                        <option key={place.id} value={place.name} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Notes</label>
                    <textarea
                      value={(userFormData as any).work_schedule_notes || ''}
                      onChange={(e) => setUserFormData({ ...userFormData, work_schedule_notes: e.target.value || null } as any)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional — e.g., back-to-back meetings Tuesdays"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={userFormData.notes || ''}
                    onChange={(e) => setUserFormData({ ...userFormData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setEditingUser(null)
                      setUserFormData({})
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveUser(user.id)}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{user.email || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <p className="font-medium">{user.phone_number || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Birthdate:</span>
                    <p className="font-medium">
                      {user.birthdate ? format(parseISO(user.birthdate), 'MMM d, yyyy') : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">School:</span>
                    <p className="font-medium">{(user as any).school || schools.find(s => s.id === user.school_id)?.name || 'Not assigned'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Primary Place:</span>
                    <p className="font-medium">{places.find(p => p.id === user.primary_place_id)?.name || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Points:</span>
                    <p className="font-medium">{user.points?.total || 0} (Level {user.points?.level || 1})</p>
                  </div>
                </div>

                {user.notes && (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">{user.notes}</p>
                  </div>
                )}

                {/* Kid Password Management - only show for non-parent users when feature is enabled */}
                {kidPasswordAuthEnabled && !user.is_parent && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Lock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Password Login</span>
                      </div>
                      {passwordManagingUser === user.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="password"
                            placeholder="New password (min 4 chars)"
                            value={newKidPassword}
                            onChange={(e) => setNewKidPassword(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => handleSetKidPassword(user.id)}
                            disabled={savingPassword || newKidPassword.length < 4}
                            className="px-3 py-1.5 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            {savingPassword ? 'Saving...' : 'Set'}
                          </button>
                          <button
                            onClick={() => {
                              setPasswordManagingUser(null)
                              setNewKidPassword('')
                            }}
                            className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {userAuthStatus[user.id]?.password_enabled ? (
                            <>
                              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Enabled</span>
                              <button
                                onClick={() => {
                                  setPasswordManagingUser(user.id)
                                  loadUserAuthStatus(user.id)
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700"
                              >
                                Reset
                              </button>
                              <button
                                onClick={() => handleDisableKidPassword(user.id)}
                                disabled={savingPassword}
                                className="text-sm text-red-600 hover:text-red-700"
                              >
                                Disable
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Not set</span>
                              <button
                                onClick={() => {
                                  setPasswordManagingUser(user.id)
                                  loadUserAuthStatus(user.id)
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700"
                              >
                                Set Password
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {userAuthStatus[user.id]?.password_enabled
                        ? `${user.display_name} can log in with username "${user.username}" and password`
                        : `Enable password login for ${user.display_name} to use on devices without Google Play`}
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => startEditUser(user)}
                    className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user)}
                    className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderPlaceCard = (place: Place) => {
    const isEditing = editingPlace === place.id
    const placeType = PLACE_TYPES.find(t => t.value === place.place_type)

    return (
      <div key={place.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={placeFormData.name || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={placeFormData.place_type || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, place_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PLACE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={placeFormData.street_address || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, street_address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={placeFormData.city || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={placeFormData.state || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={placeFormData.zip_code || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, zip_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={placeFormData.phone_number || ''}
                  onChange={(e) => setPlaceFormData({ ...placeFormData, phone_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={placeFormData.notes || ''}
                onChange={(e) => setPlaceFormData({ ...placeFormData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setEditingPlace(null)
                  setPlaceFormData({})
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={savePlace}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900">{place.name}</h3>
                <p className="text-sm text-gray-500">{placeType?.label || place.place_type}</p>
                {place.street_address && (
                  <p className="text-sm text-gray-600 mt-1">
                    {place.street_address}<br />
                    {place.city && `${place.city}, `}{place.state} {place.zip_code}
                  </p>
                )}
                {place.phone_number && (
                  <p className="text-sm text-gray-600">Phone: {place.phone_number}</p>
                )}
                {place.notes && (
                  <p className="text-sm text-gray-500 mt-2">{place.notes}</p>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEditingPlace(place.id)
                  setPlaceFormData(place)
                }}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => deletePlace(place.id)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">User & Place Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'users' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('places')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'places'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <MapPin className="w-4 h-4 inline mr-2" />
            Places
          </button>
          {/* Schools management is now in Calendar & Schools > Schools */}
        </div>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowNewUser(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add User</span>
            </button>
          </div>

          {showNewUser && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Add New Family Member</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      value={newUserData.username}
                      onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                      placeholder="e.g., emma"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, underscores only</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={newUserData.display_name}
                      onChange={(e) => setNewUserData({ ...newUserData, display_name: e.target.value })}
                      placeholder="e.g., Emma"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={newUserData.email || ''}
                      onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                      placeholder="email@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password (optional)
                    </label>
                    <input
                      type="password"
                      value={newUserData.password || ''}
                      onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                      placeholder="Leave blank for no login"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Set a password to enable login for this user</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Birthdate (optional)
                    </label>
                    <input
                      type="date"
                      value={newUserData.birthdate || ''}
                      onChange={(e) => setNewUserData({ ...newUserData, birthdate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Avatar
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {AVATAR_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setNewUserData({ ...newUserData, emoji })}
                          className={`w-10 h-10 text-xl rounded-md border-2 transition-all ${
                            newUserData.emoji === emoji
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color Theme
                    </label>
                    <input
                      type="color"
                      value={newUserData.color_theme}
                      onChange={(e) => setNewUserData({ ...newUserData, color_theme: e.target.value })}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'child', label: 'Child', desc: 'Kid account' },
                        { value: 'parent', label: 'Parent', desc: 'Full access' },
                        { value: 'caretaker', label: 'Caretaker', desc: 'Limited access' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewUserData({ ...newUserData, role: opt.value as any, is_parent: opt.value !== 'child' })}
                          className={`flex-1 px-3 py-2 rounded-md border-2 text-sm transition-all ${
                            newUserData.role === opt.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <div className="font-medium">{opt.label}</div>
                          <div className="text-xs opacity-70">{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                    {newUserData.role === 'caretaker' && (
                      <p className="text-xs text-amber-600 mt-1">Caretakers can verify chores and manage meals by default.</p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newUserData.login_enabled}
                        onChange={(e) => setNewUserData({ ...newUserData, login_enabled: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Enable Login</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowNewUser(false)
                      setNewUserData({ username: '', display_name: '', is_parent: false, role: 'child', color_theme: '#3498db', login_enabled: true, emoji: '👤' })
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createUser}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create User
                  </button>
                </div>
              </div>
            </div>
          )}

          {users.map(user => renderUserCard(user))}
        </div>
      )}

      {/* Places Tab */}
      {activeTab === 'places' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setShowNewPlace(true)
                setEditingPlace('new')
                setPlaceFormData({ place_type: 'home' })
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Place</span>
            </button>
          </div>

          {showNewPlace && editingPlace === 'new' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Add New Place</h3>
              {renderPlaceCard({ id: 'new', name: '', place_type: 'home' } as Place)}
            </div>
          )}

          {places.map(place => renderPlaceCard(place))}
        </div>
      )}

      {/* Schools management moved to Calendar & Schools > Schools */}
    </div>
  )
}